import React, { useState, useEffect } from "react";
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import './SignUp.scss';
import { LabeledContainer } from '../../components/layout';
import { Modal, Input, InputPassword } from '../../components/shared';
import { NavigationUtils} from "../../util/NavigationUtils";
import authApi from '../../api/v2/AuthApi';
import { popinApi } from '../../api/api';
import { accountApi } from '../../api/AccountApi';
import logo from '../../images/logo-teal.svg';
import bigWhiteLogo from '../../images/popin_logo_white.svg';
import password_reset_email_sent from '../../images/password_reset_email_sent.svg';
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

const SignUp = ({user}) => {

    const POPIN_TRIAL = "POPin 14-Day Trial";
    const POPIN_CREATOR = "POPin Creator";

    const [account, setAccount] = useState({
        reason: "empty",
        email: "",
        givenName: "",
        familyName: "",
        phone: "",
        password: "",
        agreement: false,
    });

    useEffect(() => {
        if (user) {
            setAccount({
                ...account,
                email: user.email,
                givenName: user.first_name,
                familyName: user.last_name,
                phone: user.phone,
            });
        }
    }, [user]);

    const [showCheckEmail, setShowCheckEmail] = useState(false);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const getYear = () => new Date().getFullYear();

    const zapTrial = (account) => {
        fetch(`https://hooks.zapier.com/hooks/catch/2071323/b5t6e3m?email=${account.email}&firstname=${account.givenName}&lastname=${account.familyName}&phone=${account.phone}&reason=${account.reason}`)
        .then((res) => {
            console.log("POPin 14-Day Trial Zapier Webhook result", res);
        })
        .catch((err) => {
            console.error("Unable to send request to POPin 14-Day Trial Zapier webhook", err);
        })
    }

    const zapCreator = (account) => {
        fetch(`https://hooks.zapier.com/hooks/catch/2071323/b5hjvfp?email=${account.email}&firstname=${account.givenName}&lastname=${account.familyName}&phone=${account.phone}&reason=${account.reason}`)
        .then((res) => {
            console.log("POPin Creator Zapier Webhook result", res);
        })
        .catch((err) => {
            console.error("Unable to send request to POPin Creator Zapier webhook", err);
        })
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (user && !user.guest) {
                // This flow is for existing logged-in users upgrading their account.
                // It does not need a blacklist check.
                const updatedUser = await accountApi.update({
                    first_name: account.givenName,
                    last_name: account.familyName,
                    password: account.password,
                    new_password: account.password,
                });
                zapTrial(account);
                await accountApi.startTrial(updatedUser);
                window.location.href = '/r/dashboard';
                return; // End execution here
            }

            // --- Start of new user signup flow ---

            // 1. Pre-check for confirmed blacklist
            try {
                console.log(`User [${account.email}] starting pre-check...`);
                await popinApi.post('/tenants/create/initiate', { email: account.email });
                console.log(`User [${account.email}] passed pre-check.`);
            } catch (preErr) {
                const message = (preErr && preErr.errorMessage) || '';
                const data = (preErr && preErr.data) || {};
                const isBlocked = (preErr && preErr.code === 403)
                  || /blacklist|blocked/i.test(message)
                  || !!(data && (data.blacklisted || data.blacklisted_domain));

                if (isBlocked) {
                    // If pre-check confirms blacklist, stop immediately.
                    const blockedMsg = 'This email address has been blocked. Please contact support.';
                    console.log(`User [${account.email}] is blocked by pre-check. Message: "${blockedMsg}"`, preErr);
                    setError(blockedMsg);
                    setLoading(false);
                    return;
                }
                // For other pre-check errors (e.g., 400 for existing user), we ignore and proceed.
                // The 'create' call is the source of truth for those cases.
                console.log(`User [${account.email}] had a non-blocking pre-check error. Proceeding to create...`, preErr);
            }

            // 2. Attempt to create the account
            console.log(`User [${account.email}] attempting to create account...`);
            await authApi.create(account);

            // 3. Handle success
            console.log(`User [${account.email}] account created successfully.`);
            setShowCheckEmail(true);
            if (account.reason === POPIN_TRIAL) {
                zapTrial(account);
            } else if (account.reason === POPIN_CREATOR) {
                zapCreator(account);
            }

        } catch (err) {
            // This single catch block now handles all errors from both pre-check and create.
            const message = (err && err.errorMessage) || '';
            const data = (err && err.data) || {};
            const isBlocked = (err && err.code === 403)
              || /blacklist|blocked/i.test(message)
              || !!(data && (data.blacklisted || data.blacklisted_domain));

            let friendlyMessage;
            if (isBlocked) {
                friendlyMessage = 'This email address has been blocked. Please contact support.';
            } else if (err && err.code === 400) {
                friendlyMessage = 'An account already exists with this email.';
            } else {
                friendlyMessage = (err && err.errorMessage) || 'Unable to process your request. Please try again.';
            }
            console.error(`User [${account.email}] signup failed. Final error:`, err);
            setError(friendlyMessage);
        } finally {
            setLoading(false);
        }
    };

    const submitEnabled = () => {
        return (account.reason !== "empty" && account.email !== "" && account.givenName !== "" && account.familyName !== "" && account.phone !== "" && account.agreement === true)
            || (user && user.email && account.givenName !== "" && account.familyName !== "" && account.password !== "" && (account.password && account.password.length >= 6) && account.phone !== "" && account.agreement === true);
    };

    return (
        <div className="signup">
            { showCheckEmail && (
                <Modal
                    isOpen={true}
                    className="container"
                    onClose={() => NavigationUtils.routeToLegacy(`/signin?email=${account.email}&returning=1`)}
                >
                    <img src={logo} alt="POPin Logo" className="logo" />
                    <p><img src={password_reset_email_sent} alt="password reset email image" className="email-icon"/></p>

                    <h1>Check your email!</h1>
                    <p>
                        You're almost there. Just follow the instructions in the account activation email we sent to
                        <br/>
                        <span>{account.email}</span>
                        <br/>
                        from popinnow.com
                    </p>
                    <hr/>
                    <p>If you don't see an email from us, be sure to check your spam folder.</p>
                    <p>Having trouble? Click the icon in the bottom right-hand corner of your screen to chat with support</p>
                </Modal>
            )}
            <div layout="row" layout-align="center center" className="banner">Launch a POPin Session Today!</div>
            <div className="container signup-box">
                <div className="row">
                    <div className="col-sm signup-form text-left">
                        <p>You are seconds away from launching<br/>your first session... but first:</p>
                        <form autoComplete="off">
                        { (!user || user.guest) &&
                            <LabeledContainer
                                titleWarning={false}
                                title={'Why are you here?'}
                                className="mb-3"
                            >
                                <select
                                    name="reason"
                                    className="dropdown select"
                                    style={{ 'padding': 7 }}
                                    value={account.reason}
                                    onChange={e => setAccount({ ...account, reason: e.target.value})}
                                >
                                    <option value="empty">[Select Answer]</option>
                                    <option value={POPIN_TRIAL}>I am new to POPin &amp; want to try it out for FREE</option>
                                    <option value={POPIN_CREATOR}>My organization uses POPin &amp; I want to launch my own sessions</option>
                                </select>
                            </LabeledContainer>
                        }
                        {
                            (user && !user.guest) &&
                            <input type="hidden" name="reason" value={POPIN_CREATOR}/>
                        }
                            <LabeledContainer
                                titleWarning={false}
                                title={'Email address:'}
                                className="mb-3"
                            >
                                <Input
                                    name="email"
                                    size="30"
                                    placeholder={''}
                                    value={account.email}
                                    disabled={user && !user.guest && user.email}
                                    onChange={e => { setAccount({ ...account, email: e.target.value}); setError(''); }}
                                />
                            </LabeledContainer>
                            <LabeledContainer
                                titleWarning={false}
                                title={'First name:'}
                                className="mb-3"
                            >
                                <Input
                                    name="givenName"
                                    size="30"
                                    placeholder={''}
                                    value={account.givenName}
                                    onChange={e => setAccount({ ...account, givenName: e.target.value})}
                                    autoComplete="off"
                                />
                            </LabeledContainer>
                            <LabeledContainer
                                titleWarning={false}
                                title={'Last name:'}
                                className="mb-3"
                            >
                                <Input
                                    name="familyName"
                                    size="30"
                                    placeholder={''}
                                    value={account.familyName}
                                    onChange={e => setAccount({ ...account, familyName: e.target.value})}
                                    autoComplete="off"
                                />
                            </LabeledContainer>
                            <LabeledContainer
                                titleWarning={false}
                                title={'Phone:'}
                                className="mb-3"
                            >
                                <PhoneInput
                                    name="phone"
                                    size="20"
                                    placeholder={''}
                                    defaultCountry="US"
                                    value={account.phone}
                                    onChange={(value) => setAccount({ ...account, phone: value})}
                                    autoComplete="off"
                                    // className="form-control"
                                />
                            </LabeledContainer>

                            { (user && !user.guest) &&
                            <LabeledContainer
                            titleWarning={false}
                            title={'Create Password:'}
                            className="mb-3"
                            >
                                <InputPassword
                                    name="password"
                                    size="30"
                                    placeholder={'Passwords must be at least 6 characters long'}
                                    value={account.password}
                                    onChange={e => setAccount({ ...account, password: e.target.value })}
                                    autoComplete="off"
                                />
                                {/* Passwords must be at least 6 characters long */}
                            </LabeledContainer>
                            }

                            <label style={{ 'marginTop': 20 }}>
                                <input
                                    type="checkbox"
                                    name="agreement"
                                    style={ {'marginRight': 10} }
                                    checked={account.agreement}
                                    onChange={() => setAccount({ ...account, agreement: !account.agreement})}
                                    autoComplete="off"
                                />
                                I agree to POPin's <a target="#" href="https://www.trypopin.com/popin-terms-of-service">Terms of Service</a> and <a target="#" href="https://www.trypopin.com/popin-privacy-policy">Privacy Policy</a>.
                            </label>

                            {
                                error && (
                                    <div className="error">
                                        {error}
                                        {
                                            error === 'An account already exists with this email.' && (
                                                <span>
                                                    &nbsp;
                                                    You can <a href="#" onClick={() => NavigationUtils.routeToLegacy(`/signin?email=${account.email}&returning=1`)}>sign in</a> or
                                                    use the <a href="#" onClick={() => NavigationUtils.routeToLegacy(`/reset-password?email=${account.email}`)}>forgot password</a> page to reset your password.
                                                </span>
                                            )
                                        }
                                    </div>
                                )
                            }

                            <div style={{ 'marginTop': 20 }}>
                                <button
                                    className="btn-primary"
                                    disabled={!submitEnabled() || loading}
                                    onClick={onSubmit}>
                                    {loading ? 'Processing...' : 'Create Account'}
                                </button>
                            </div>
                        </form>

                        <p style={{'marginTop': 30}}>
                            <a target="#" href="https://www.trypopin.com">About Us</a>
                        </p>

                        <p>
                            Copyright &copy; {getYear()} Change Needs Leaders, Inc. All rights reserved.
                        </p>
                    </div>
                    <div className="col-sm features">
                        <p>Your account includes the following features:</p>
                        <ul>
                            <li><i className="material-icons c-white mr-10">check</i> Crowdsource &amp; Solve</li>
                            <li><i className="material-icons c-white mr-10">check</i> Survey</li>
                            <li><i className="material-icons c-white mr-10">check</i> Live (Poll)</li>
                            <li><i className="material-icons c-white mr-10">check</i> Prioritize</li>
                            <li><i className="material-icons c-white mr-10">check</i> Client Success Support</li>
                        </ul>
                        <hr/>
                        <strong>Need more info?</strong>
                        <p>Use the live Help chat to contact our team!</p>

                        <img style={{marginTop: '50px'}} src={bigWhiteLogo} alt="POPin Big White Logo" width="150" />
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = state => {
    return {
        user: state.user.user
    };
};

export default withRouter(connect(mapStateToProps, {})(SignUp));
